import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, ChevronRight,
  RefreshCw, Download, PieChart, BarChart3, Shield, Calendar, Minus,
  ArrowUpRight, ArrowDownRight, Info
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * ScopeCoverageWidget - PMO Dashboard widget for baseline adoption rates
 * Shows coverage by Portfolio and Program with trend charts
 */
const ScopeCoverageWidget = ({ className = '' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [viewMode, setViewMode] = useState('portfolio'); // 'portfolio' | 'program'
  const [timeRange, setTimeRange] = useState(7); // 7, 30, 90 days
  const [showChart, setShowChart] = useState(true);
  const [error, setError] = useState(null);

  const tenantId = user?.tenant?.id || user?.tenant_id;

  // Cache for trend data
  const trendCache = useMemo(() => new Map(), []);

  useEffect(() => {
    if (tenantId) {
      loadCoverage();
    } else {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId && showChart) {
      loadTrends();
    }
  }, [tenantId, timeRange, viewMode, showChart]);

  const loadCoverage = async () => {
    if (!tenantId) return;
    
    try {
      setLoading(true);
      setError(null);
      const res = await api.getScopeCoverage(tenantId);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load scope coverage:', err);
      setError('Unable to load scope coverage data');
    } finally {
      setLoading(false);
    }
  };

  const loadTrends = async () => {
    if (!tenantId) return;
    
    // Check cache first (for 7 days only)
    const cacheKey = `${timeRange}-${viewMode}`;
    if (timeRange === 7 && trendCache.has(cacheKey)) {
      setTrendData(trendCache.get(cacheKey));
      return;
    }
    
    try {
      setTrendLoading(true);
      const res = await api.getScopeCoverageTrends(tenantId, timeRange, viewMode);
      setTrendData(res.data);
      
      // Cache 7-day data
      if (timeRange === 7) {
        trendCache.set(cacheKey, res.data);
      }
    } catch (err) {
      console.error('Failed to load trend data:', err);
    } finally {
      setTrendLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!data && !trendData) return;
    
    let csvContent = '';
    
    // If showing chart, export trend data
    if (showChart && trendData?.trend_data) {
      const headers = ['Date', 'Coverage %', 'Approved', 'Pending', 'No Baseline', 'Total', 'Is Anomaly'];
      csvContent = headers.join(',') + '\n';
      trendData.trend_data.forEach(row => {
        csvContent += [
          row.date,
          row.coverage_pct,
          row.approved_count,
          row.candidate_pending_count,
          row.no_baseline_count,
          row.total_projects,
          row.is_anomaly ? 'Yes' : 'No'
        ].join(',') + '\n';
      });
    } else if (data) {
      // Export portfolio/program data
      const items = viewMode === 'portfolio' ? data.by_portfolio : data.by_program;
      const headers = ['Name', 'Total Projects', 'Baselined', 'Pending', 'Coverage %', 'Status'];
      csvContent = headers.join(',') + '\n';
      items.forEach(item => {
        csvContent += [
          item.name,
          item.total_projects,
          item.baselined,
          item.pending,
          item.coverage_pct,
          item.status
        ].join(',') + '\n';
      });
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scope-coverage-${showChart ? 'trends' : viewMode}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'at_risk':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'needs_attention':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Target className="w-4 h-4 text-gray-400" />;
    }
  };

  const handleChartClick = useCallback((data) => {
    if (data?.activePayload?.[0]?.payload) {
      const point = data.activePayload[0].payload;
      // Navigate to scope baselines filtered by date range ending at clicked point
      navigate(`/scope-baselines?date=${point.date}`);
    }
  }, [navigate]);

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
        <div className="font-medium text-gray-900 mb-2">{label}</div>
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-600">Coverage:</span>
            <span className="font-semibold text-blue-600">{data.coverage_pct}%</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-600">Approved:</span>
            <span className="text-green-600">{data.approved_count}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-600">Pending:</span>
            <span className="text-amber-600">{data.candidate_pending_count}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-600">No Baseline:</span>
            <span className="text-gray-500">{data.no_baseline_count}</span>
          </div>
          {data.is_anomaly && (
            <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-1 text-red-600">
              <AlertTriangle className="w-3 h-3" />
              <span className="text-xs">Coverage drop detected</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render loading state
  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${className}`}>
        <div className="p-6 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${className}`}>
        <div className="p-6 text-center text-red-500">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">{error}</p>
          <button 
            onClick={loadCoverage}
            className="mt-2 text-sm text-blue-600 hover:underline"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const currentData = viewMode === 'portfolio' ? data?.by_portfolio : data?.by_program;

  return (
    <div className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${className}`} data-testid="scope-coverage-widget">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <Target className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Scope Coverage</h3>
            <p className="text-xs text-gray-500">Baseline adoption across {viewMode}s</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Chart/List toggle */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setShowChart(true)}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                showChart ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="chart-view-toggle"
            >
              <TrendingUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setShowChart(false)}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                !showChart ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="list-view-toggle"
            >
              <BarChart3 className="w-3.5 h-3.5" />
            </button>
          </div>
          
          <button
            onClick={handleExportCSV}
            className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
            title="Export CSV"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Row */}
      {data?.overall && (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 grid grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500">Total</div>
            <div className="text-lg font-bold text-gray-900">{data.overall.total}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Baselined</div>
            <div className="text-lg font-bold text-green-600">{data.overall.baselined}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Pending</div>
            <div className="text-lg font-bold text-amber-600">{data.overall.pending}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Coverage</div>
            <div className="flex items-center gap-1">
              <span className="text-lg font-bold text-purple-600">{data.overall.coverage_pct}%</span>
              {trendData?.summary?.coverage_change !== undefined && trendData.summary.coverage_change !== 0 && (
                <span className={`text-xs flex items-center ${
                  trendData.summary.coverage_change > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {trendData.summary.coverage_change > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {Math.abs(trendData.summary.coverage_change)}%
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toggle & Time Range */}
      <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('portfolio')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              viewMode === 'portfolio' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="portfolio-toggle"
          >
            Portfolios
          </button>
          <button
            onClick={() => setViewMode('program')}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              viewMode === 'program' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid="program-toggle"
          >
            Programs
          </button>
        </div>

        {showChart && (
          <div className="flex items-center gap-1">
            {[7, 30, 90].map(days => (
              <button
                key={days}
                onClick={() => setTimeRange(days)}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  timeRange === days 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                data-testid={`range-${days}-btn`}
              >
                {days}d
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Chart View */}
      {showChart && (
        <div className="p-4">
          {trendLoading ? (
            <div className="h-48 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : trendData?.trend_data?.length > 0 ? (
            <div className="h-48" data-testid="coverage-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={trendData.trend_data} 
                  onClick={handleChartClick}
                  margin={{ top: 5, right: 5, bottom: 5, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }} 
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="coverage_pct" 
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#8b5cf6' }}
                    activeDot={{ r: 5, fill: '#8b5cf6' }}
                  />
                  {/* Anomaly markers */}
                  {trendData.trend_data.filter(d => d.is_anomaly).map((point, idx) => (
                    <ReferenceDot
                      key={idx}
                      x={point.date}
                      y={point.coverage_pct}
                      r={6}
                      fill="#ef4444"
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-gray-400">
              <Calendar className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">No trend history for this period</p>
              <p className="text-xs text-gray-400">Data will appear as baselines are approved</p>
            </div>
          )}

          {/* Trend Summary */}
          {trendData?.summary && (
            <div className="mt-3 flex items-center justify-between text-xs">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Change:</span>
                  <span className={`font-medium ${
                    trendData.summary.coverage_change > 0 ? 'text-green-600' : 
                    trendData.summary.coverage_change < 0 ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {trendData.summary.coverage_change > 0 ? '+' : ''}{trendData.summary.coverage_change}%
                  </span>
                </div>
                {trendData.summary.anomaly_count > 0 && (
                  <div className="flex items-center gap-1 text-red-600">
                    <AlertTriangle className="w-3 h-3" />
                    <span>{trendData.summary.anomaly_count} coverage drop{trendData.summary.anomaly_count > 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate('/scope-baselines')}
                className="text-purple-600 hover:text-purple-800 flex items-center gap-1"
              >
                View All <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {!showChart && (
        <div className="max-h-52 overflow-y-auto">
          {!currentData || currentData.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No {viewMode}s found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {currentData.map((item, idx) => (
                <button
                  key={item.id || idx}
                  onClick={() => navigate(`/scope-baselines?${viewMode}=${item.id}`)}
                  className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                  data-testid={`coverage-item-${item.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {getStatusIcon(item.status)}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">{item.name}</div>
                      <div className="text-xs text-gray-500">
                        {item.baselined}/{item.total_projects} projects baselined
                      </div>
                    </div>
                  </div>
                  
                  {/* Coverage bar */}
                  <div className="flex items-center gap-2 ml-3">
                    <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          item.coverage_pct >= 80 ? 'bg-green-500' : 
                          item.coverage_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${item.coverage_pct}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium w-10 text-right ${
                      item.coverage_pct >= 80 ? 'text-green-600' : 
                      item.coverage_pct >= 50 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {item.coverage_pct}%
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Top 5 Lowest Coverage Section */}
      {data?.low_coverage_portfolios?.length > 0 && (
        <div className="border-t border-gray-200" data-testid="lowest-coverage-section">
          <div className="p-3 bg-red-50 border-b border-red-100">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-700">Top 5 Lowest Coverage</span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {data.low_coverage_portfolios.slice(0, 5).map((item, idx) => (
              <button
                key={item.id || idx}
                onClick={() => navigate(`/scope-baselines?portfolio=${item.id}`)}
                className="w-full px-3 py-2 flex items-center justify-between hover:bg-red-50 transition-colors text-left"
                data-testid={`low-coverage-${item.id}`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-xs text-red-500 font-bold w-4">{idx + 1}</span>
                  <span className="text-sm text-gray-700 truncate">{item.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{item.baselined}/{item.total_projects}</span>
                  <span className="text-sm font-bold text-red-600">{item.coverage_pct}%</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
        <button
          onClick={() => navigate('/scope-baselines')}
          className="text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
          data-testid="view-all-baselines-link"
        >
          View All Scope Baselines
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ScopeCoverageWidget;
